/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import {translate} from '@ion/core/src/locale/locale';
import Switch from '@ion/dls/src/components/switch/Switch';
import {GroupFormFields} from '@ion/dls/src/editors/GroupEditorUtils';
import Render from '@ion/dls/src/render/Render';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import AccountTooltip from './AccountTooltip';

interface AccountTypeProps {
  classNames?: ClassNameMap<string>;
  handleSwitchChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  isLoggedinUserAccount?: boolean;
  isCalampSupport?: boolean;
  isEnterprise?: boolean;
  isViewMode?: boolean;
  isEditMode?: boolean;
  isPhone?: boolean;
};

interface StyleProps {
  isEnterprise?: boolean;
};

const useStyles = makeStyles(() => {
  return {
    wrapperStyle : {
      display    : 'flex',
      alignItems : 'center',
      gap        : '4px'
    },
    labelContainer : {
      width           : '136px',
      height          : '24px',
      opacity         : 1,
      borderRadius    : '4px',
      display         : 'flex',
      alignItems      : 'center',
      justifyContent  : 'center',
      fontFamily      : 'Roboto !important',
      fontWeight      : 500,
      fontSize        : '10px',
      lineHeight      : '16px',
      letterSpacing   : '1px',
      textTransform   : 'uppercase',
      color           : 'rgba(51, 51, 51, 1)',
      backgroundColor : ({isEnterprise}: StyleProps) => isEnterprise ? 'rgba(105, 178, 70, 0.3)' : 'rgba(224, 224, 224, 1)'
    }
  };
});

/**
 * Displays and controls whether an account is marked as Enterprise or Standard,
 * allowing switching when editing and showing a tooltip when in view mode.
 */
export const AccountType = ({
  classNames,
  handleSwitchChange,
  isLoggedinUserAccount = false,
  isCalampSupport = false,
  isEnterprise = false,
  isViewMode = false,
  isEditMode = false,
  isPhone = false
}: AccountTypeProps) => {
  const classes = useStyles({isEnterprise});

  const handleLabelChange = useCallback(() => {
    if (isEnterprise) {
      return translate('admin-groups:dialog.accountType.label.enterpriseAccount');
    }
    return translate('admin-groups:dialog.accountType.label.standardAccount');
  }, [isEnterprise]);

  return (
    <>
      <BodyTypography className={classNames.fieldLabel}>
        {translate('admin-groups:dialog.accountType.title')}
      </BodyTypography>
      <div className={classes.wrapperStyle}>
        <Render>
          <Render.If condition={!isViewMode}>
            <div className={classNames.switch}>
              <div className={classNames.overridesSwitches}>
                <Switch label={handleLabelChange()}
                  marginRight={isPhone ? '25px' : '0px'}
                  name={GroupFormFields.Enterprise}
                  onChange={handleSwitchChange}
                  disabled={isViewMode ||
                    ((isEditMode && isCalampSupport) ? false : isEditMode) ||
                    isLoggedinUserAccount}
                  checked={isEnterprise} />
              </div>
            </div>
          </Render.If>
          <Render.If condition={isViewMode}>
            <div className={classes.labelContainer}>
              {handleLabelChange()}
            </div>
          </Render.If>
        </Render>
        <AccountTooltip title={translate('admin-groups:dialog.tooltips.enterpriseToolTip')} align />
      </div>
    </>
  );
};

export default AccountType;
